import { FC, useState, useRef, KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Badge } from './badge';
import { cn } from '@/lib/utils';

interface TagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  maxTags?: number;
}

export const TagInput: FC<TagInputProps> = ({
  value,
  onChange,
  placeholder = 'Type and press Enter (comma also separates)...',
  disabled = false,
  error,
  maxTags,
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed) return;

    // Case-insensitive duplicate check
    const exists = value.some((t) => t.toLowerCase() === trimmed.toLowerCase());
    if (exists) return;

    // Check max limit
    if (maxTags && value.length >= maxTags) return;

    onChange([...value, trimmed]);
    setInputValue('');
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((t) => t !== tagToRemove));
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  };

  return (
    <div className='space-y-1'>
      <div
        className={cn(
          'flex flex-wrap gap-2 border rounded-md py-2 px-3 min-h-9 bg-background',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
          error && 'border-rose-600 dark:border-rose-400',
          disabled && 'opacity-50 cursor-not-allowed bg-muted',
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {value.map((tag) => (
          <Badge key={tag} variant='secondary' className='gap-1 pr-1'>
            <span>{tag}</span>
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag);
              }}
              disabled={disabled}
              aria-label={`Remove ${tag}`}
              className='hover:bg-secondary-foreground/20 rounded-sm p-0.5'
            >
              <X className='h-3 w-3' />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          type='text'
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          className='flex-1 min-w-[120px] outline-none bg-transparent text-base md:text-sm  placeholder:text-muted-foreground'
        />
      </div>
      {error && (
        <p className='text-xs text-rose-600 dark:text-rose-400 break-words'>
          {error}
        </p>
      )}
    </div>
  );
};
